import { file as sdkFileHelper } from './sdkLoader';

export const MESSAGE_ATTACHMENT_MAX_SIZE = 1024 * 1024 * 1024;

export const MESSAGE_ATTACHMENT_ACCEPT =
  'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.odp';

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/rtf',
  'text/rtf',
  'text/plain',
  'text/csv',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
];

const DOCUMENT_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'rtf',
  'odt',
  'ods',
  'odp',
];

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'heic'];

const EXECUTABLE_EXTENSIONS = [
  'ade',
  'adp',
  'apk',
  'app',
  'bat',
  'bin',
  'cmd',
  'com',
  'cpl',
  'dll',
  'dmg',
  'exe',
  'gadget',
  'hta',
  'ins',
  'ipa',
  'iso',
  'jar',
  'js',
  'jse',
  'lib',
  'lnk',
  'mde',
  'msc',
  'msi',
  'msp',
  'mst',
  'ps1',
  'scr',
  'sh',
  'sys',
  'vb',
  'vbe',
  'vbs',
  'ws',
  'wsc',
  'wsf',
  'wsh',
];

const EXECUTABLE_MIME_TYPES = [
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/x-executable',
  'application/x-sh',
  'application/x-csh',
  'application/java-archive',
  'application/vnd.android.package-archive',
];

export const ATTACHMENT_TYPE_FILE = 'file';
export const ATTACHMENT_TYPE_IMAGE = 'image';

const OWN_FILE_REFRESH_DELAYS = [0, 1000, 3000];

const idString = id => id?.uuid || id;

export const isMessageImageFile = selectedFile => {
  return typeof selectedFile?.type === 'string' && selectedFile.type.startsWith('image/');
};

export const attachmentFileIdString = attachment => {
  const type = attachment?.type;
  return idString(
    attachment?.fileId ||
      attachment?.attributes?.fileId ||
      attachment?.file?.id ||
      attachment?.attributes?.file?.id ||
      (type === 'file' || type === 'ownFile' ? attachment?.id : null)
  );
};

export const attachmentImageIdString = attachment => {
  const type = attachment?.type;
  return idString(
    attachment?.imageId ||
      attachment?.attributes?.imageId ||
      attachment?.image?.id ||
      attachment?.attributes?.image?.id ||
      (type === 'image' ? attachment?.id : null)
  );
};

export const attachmentFileAttachmentIdString = attachment => {
  return idString(
    attachment?.fileAttachmentId ||
      attachment?.attachmentId ||
      attachment?.attributes?.fileAttachmentId ||
      attachment?.attributes?.attachmentId ||
      (attachment?.type === 'fileAttachment' ? attachment?.id : null)
  );
};

export const messageAttachmentIdString = attachmentOrId => {
  const attachment = attachmentOrId || {};
  return idString(
    attachmentFileIdString(attachment) ||
      attachmentImageIdString(attachment) ||
      attachmentFileAttachmentIdString(attachment) ||
      attachment?.id ||
      attachmentOrId
  );
};

const fileExtension = name => {
  const parts = String(name || '').split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const isValidFilename = name => {
  return (
    typeof name === 'string' &&
    name.trim().length > 0 &&
    name.length <= 255 &&
    !/[\\/\u0000-\u001f\u007f]/.test(name)
  );
};

const isAllowedDocumentOrImage = selectedFile => {
  const mimeType = (selectedFile?.type || '').toLowerCase();
  const extension = fileExtension(selectedFile?.name);

  return (
    isMessageImageFile(selectedFile) ||
    DOCUMENT_MIME_TYPES.includes(mimeType) ||
    IMAGE_EXTENSIONS.includes(extension) ||
    DOCUMENT_EXTENSIONS.includes(extension)
  );
};

const isExecutableFile = selectedFile => {
  const mimeType = (selectedFile?.type || '').toLowerCase();
  const extension = fileExtension(selectedFile?.name);

  return EXECUTABLE_MIME_TYPES.includes(mimeType) || EXECUTABLE_EXTENSIONS.includes(extension);
};

export const getMessageAttachmentValidationError = (selectedFile, intl) => {
  const error = (key, fallback) =>
    intl ? intl.formatMessage({ id: `MessageAttachments.${key}` }) : fallback;
  if (!selectedFile) {
    return null;
  }

  if (!isValidFilename(selectedFile.name)) {
    return error('invalidName', 'Choose a file with a valid filename.');
  }

  if (typeof selectedFile.size !== 'number' || selectedFile.size <= 0) {
    return error('emptyFile', 'Choose a non-empty file.');
  }

  if (selectedFile.size > MESSAGE_ATTACHMENT_MAX_SIZE) {
    return error('tooLarge', 'Files must be 1 GB or smaller.');
  }

  if (isExecutableFile(selectedFile)) {
    return error('executable', 'Executable files are not allowed.');
  }

  if (!isAllowedDocumentOrImage(selectedFile)) {
    return error('unsupported', 'Only documents and images can be uploaded.');
  }

  return null;
};

export const formatFileSize = size => {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '';
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const fractionDigits = Number.isInteger(value) || value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(fractionDigits)} ${units[unitIndex]}`;
};

export const formatAttachmentFileType = selectedFile => {
  const mimeType = selectedFile?.type;
  if (mimeType) {
    return mimeType;
  }

  const extension = fileExtension(selectedFile?.name);
  return extension ? extension.toUpperCase() : 'File';
};

const uploadAttributes = uploadResponse => {
  return uploadResponse?.data?.data?.attributes || uploadResponse?.data?.attributes || {};
};

const ownFileAttributes = ownFile => ownFile?.attributes || {};

const fileVerificationStatus = ownFile => {
  const attributes = ownFileAttributes(ownFile);
  return attributes.verificationStatus || attributes.status || attributes.state || '';
};

const wait = ms => (ms > 0 ? new Promise(resolve => setTimeout(resolve, ms)) : Promise.resolve());

const isPendingUploadStatus = status => {
  return (
    String(status || '')
      .toLowerCase()
      .replace(/[\s_-]/g, '') === 'pendingupload'
  );
};

const refreshOwnFile = ({ sdk, fileId, attempt = 0 }) => {
  if (!sdk?.ownFiles?.show) {
    return Promise.resolve(null);
  }

  const refreshDelay = OWN_FILE_REFRESH_DELAYS[attempt] || 0;

  return wait(refreshDelay)
    .then(() => sdk.ownFiles.show({ id: fileId }, { expand: true }))
    .then(response => {
      const ownFile = response?.data?.data || null;
      const status = fileVerificationStatus(ownFile);
      const hasMoreAttempts = attempt < OWN_FILE_REFRESH_DELAYS.length - 1;

      return isPendingUploadStatus(status) && hasMoreAttempts
        ? refreshOwnFile({ sdk, fileId, attempt: attempt + 1 })
        : ownFile;
    })
    .catch(() => null);
};

const imageVariantData = image => image?.attributes?.variants || {};

const uploadImageAttachment = ({ sdk, selectedFile, onUploadProgress }) => {
  if (!sdk?.images?.upload) {
    return Promise.reject(new Error('Image uploads are not supported by this Sharetribe SDK.'));
  }

  return sdk.images
    .upload({ image: selectedFile }, { expand: true }, { onUploadProgress })
    .then(imageUploadResponse => {
      const uploadedImage = imageUploadResponse?.data?.data;
      const imageId = uploadedImage?.id;

      if (!imageId) {
        throw new Error('Image upload failed.');
      }

      return {
        type: ATTACHMENT_TYPE_IMAGE,
        imageId,
        metadata: {
          type: ATTACHMENT_TYPE_IMAGE,
          imageId: messageAttachmentIdString(imageId),
          name: selectedFile.name,
          mimeType: selectedFile.type,
          size: selectedFile.size,
          variants: imageVariantData(uploadedImage),
          uploadedAt: new Date().toISOString(),
        },
      };
    });
};

const uploadFileAttachment = ({ sdk, selectedFile, onUploadProgress }) => {
  if (!sdk?.ownFiles?.create || !sdk?.fileUploads?.create || !sdkFileHelper?.upload) {
    return Promise.reject(new Error('File uploads are not supported by this Sharetribe SDK.'));
  }

  const metadata = sdkFileHelper.metadata(selectedFile);

  return sdk.ownFiles.create(metadata, { expand: true }).then(ownFileResponse => {
    const ownFile = ownFileResponse?.data?.data;
    const fileId = ownFile?.id;

    if (!fileId) {
      throw new Error('File registration failed.');
    }

    return sdk.fileUploads.create({ fileId }).then(uploadResponse => {
      const { method, url, headers } = uploadAttributes(uploadResponse);

      return sdkFileHelper
        .upload({
          method,
          url,
          headers,
          file: selectedFile,
          onUploadProgress,
        })
        .then(() => refreshOwnFile({ sdk, fileId }))
        .then(refreshedOwnFile => {
          const uploadedOwnFile = refreshedOwnFile || ownFile;
          const status = fileVerificationStatus(uploadedOwnFile);
          const verificationStatus = isPendingUploadStatus(status)
            ? 'available'
            : status || 'pending';
          return {
            type: ATTACHMENT_TYPE_FILE,
            fileId,
            metadata: {
              type: ATTACHMENT_TYPE_FILE,
              fileId: messageAttachmentIdString(fileId),
              name: metadata.name,
              mimeType: metadata.mimeType,
              size: metadata.size,
              verificationStatus,
              uploadedAt: new Date().toISOString(),
            },
          };
        });
    });
  });
};

export const uploadMessageAttachment = ({ sdk, selectedFile, onUploadProgress }) => {
  const validationError = getMessageAttachmentValidationError(selectedFile);
  if (validationError) {
    return Promise.reject(new Error(validationError));
  }

  return isMessageImageFile(selectedFile)
    ? uploadImageAttachment({ sdk, selectedFile, onUploadProgress })
    : uploadFileAttachment({ sdk, selectedFile, onUploadProgress });
};

export const attachmentMimeType = attachment => {
  return (
    attachment?.mimeType ||
    attachment?.attributes?.mimeType ||
    attachment?.file?.attributes?.mimeType ||
    attachment?.attributes?.file?.attributes?.mimeType ||
    ''
  );
};

const attachmentVariants = attachment => {
  return (
    attachment?.variants ||
    attachment?.attributes?.variants ||
    attachment?.image?.attributes?.variants ||
    attachment?.attributes?.image?.attributes?.variants ||
    {}
  );
};

const variantUrl = variant => {
  return typeof variant?.url === 'string' ? variant.url : null;
};

export const attachmentImageUrl = attachment => {
  const variants = attachmentVariants(attachment);
  const preferredVariantNames = [
    'square-small2x',
    'square-small',
    'default',
    'scaled-medium',
    'scaled-small',
  ];

  const preferredUrl = preferredVariantNames.map(name => variantUrl(variants[name])).find(Boolean);
  if (preferredUrl) {
    return preferredUrl;
  }

  return (
    Object.values(variants)
      .map(variantUrl)
      .find(Boolean) || null
  );
};

export const isImageAttachment = attachment => {
  return (
    attachment?.type === ATTACHMENT_TYPE_IMAGE ||
    attachment?.attachmentType === ATTACHMENT_TYPE_IMAGE ||
    !!attachmentImageIdString(attachment) ||
    !!attachmentImageUrl(attachment)
  );
};

export const attachmentFileName = attachment => {
  return (
    attachment?.name ||
    attachment?.attributes?.name ||
    attachment?.file?.attributes?.name ||
    attachment?.attributes?.file?.attributes?.name ||
    'Attachment'
  );
};

export const attachmentFileSize = attachment => {
  return (
    attachment?.size ||
    attachment?.attributes?.size ||
    attachment?.file?.attributes?.size ||
    attachment?.attributes?.file?.attributes?.size ||
    null
  );
};

export const attachmentVerificationStatus = attachment => {
  return (
    attachment?.verificationStatus ||
    attachment?.attributes?.verificationStatus ||
    attachment?.attributes?.status ||
    attachment?.attributes?.state ||
    attachment?.file?.attributes?.verificationStatus ||
    attachment?.file?.attributes?.status ||
    attachment?.file?.attributes?.state ||
    ''
  );
};

export const attachmentAvailability = attachment => {
  const status = String(attachmentVerificationStatus(attachment)).toLowerCase();

  if (isPendingUploadStatus(status)) {
    return 'available';
  }

  if (/fail|reject|malware|blocked|denied|unavailable/.test(status)) {
    return 'failed';
  }

  if (
    /pending|process|upload|created|verify/.test(status) &&
    !/verified|approved|success/.test(status)
  ) {
    return 'pending';
  }

  return 'available';
};

const messageDirectAttachments = message => {
  const attributes = message?.attributes || {};
  return (
    attributes.publicFileAttachments ||
    attributes.fileAttachments ||
    message?.publicFileAttachments ||
    message?.fileAttachments ||
    []
  );
};

const metadataAttachments = (transaction, messageId) => {
  const attachments = transaction?.attributes?.metadata?.messageAttachments;
  const messageIdString = idString(messageId);

  return Array.isArray(attachments)
    ? attachments.filter(attachment => idString(attachment.messageId) === messageIdString)
    : [];
};

const attachmentType = attachment => {
  return isImageAttachment(attachment) ? ATTACHMENT_TYPE_IMAGE : ATTACHMENT_TYPE_FILE;
};

const attachmentMergeKey = attachment => {
  const type = attachmentType(attachment);
  const attachmentId =
    type === ATTACHMENT_TYPE_IMAGE
      ? attachmentImageIdString(attachment)
      : attachmentFileIdString(attachment) || attachmentFileAttachmentIdString(attachment);

  return attachmentId ? `${type}:${attachmentId}` : null;
};

export const messageAttachmentDownloadParams = attachment => {
  const fileAttachmentId = attachmentFileAttachmentIdString(attachment);
  const fileId = attachmentFileIdString(attachment);

  if (fileAttachmentId) {
    return { fileAttachmentId };
  }

  return fileId ? { fileId } : null;
};

export const getMessageAttachments = (message, transaction) => {
  const directAttachments = messageDirectAttachments(message);
  const storedAttachments = metadataAttachments(transaction, message?.id);
  const attachmentsById = {};

  [
    ...storedAttachments.map(attachment => ({ attachment, isDirect: false })),
    ...directAttachments.map(attachment => ({ attachment, isDirect: true })),
  ].forEach(({ attachment, isDirect }) => {
    const key = attachmentMergeKey(attachment);
    if (!key) {
      return;
    }

    const attachmentData = typeof attachment === 'object' && attachment !== null ? attachment : {};
    const previousAttachment = attachmentsById[key] || {};
    const type = attachmentType(attachment);
    const directAttachmentStatus = isDirect ? attachmentVerificationStatus(attachment) : '';
    const nextAttachment = {
      ...previousAttachment,
      ...attachmentData,
      type,
      ...(type === ATTACHMENT_TYPE_IMAGE
        ? { imageId: attachmentImageIdString(attachment) }
        : {
            fileId: attachmentFileIdString(attachment),
            fileAttachmentId: attachmentFileAttachmentIdString(attachment),
          }),
      hasDirectAttachment: previousAttachment.hasDirectAttachment || isDirect,
    };

    if (type === ATTACHMENT_TYPE_FILE && isDirect && !directAttachmentStatus) {
      nextAttachment.verificationStatus = 'available';
    }

    attachmentsById[key] = {
      ...nextAttachment,
    };
  });

  return Object.values(attachmentsById);
};

export const getMessageFileAttachments = getMessageAttachments;
